import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  EMPTY_PJ_AGRADA_FORM,
  type PjAgrada,
  type PjAgradaFormData,
  type PjAgradaStatus,
} from '@/components/PjAgrada/pjAgradaTypes'
import { pjAgradaService } from '@/services/pjAgradaService'
import { getFriendlyError } from '@/utils/getFriendlyError'

export type PjAgradaFilter = PjAgradaStatus | 'todos'

export function usePjAgradaPage() {
  const [prestadores, setPrestadores] = useState<PjAgrada[]>([])
  const [stats, setStats] = useState({
    ativos: 0,
    inativos: 0,
    naoPreenchidos: 0,
    naoConcluidos: 0,
    total: 0,
  })
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<PjAgradaFilter>('todos')
  const [showFormModal, setShowFormModal] = useState(false)
  const [formData, setFormData] = useState<PjAgradaFormData>(EMPTY_PJ_AGRADA_FORM)
  const [saving, setSaving] = useState(false)
  const [selected, setSelected] = useState<PjAgrada | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await pjAgradaService.list({ search: search || undefined, status: filter })
      setPrestadores(result.data)
      setStats(result.meta.stats)
    } catch (err: unknown) {
      setPrestadores([])
      toast.error(getFriendlyError(err, 'Erro ao carregar funcionários PJ'))
    } finally {
      setLoading(false)
    }
  }, [filter, search])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void load()
    }, search ? 300 : 0)
    return () => window.clearTimeout(timer)
  }, [load, search])

  const filtered = useMemo(() => prestadores, [prestadores])

  const openForm = (prestador?: PjAgrada) => {
    if (!prestador) {
      setFormData(EMPTY_PJ_AGRADA_FORM)
    } else {
      setFormData({
        id: prestador.id,
        nomeCompleto: prestador.nomeCompleto,
        email: prestador.email ?? '',
        telefone: prestador.telefone,
        cpf: prestador.cpf ?? '',
        cnpj: prestador.cnpj ?? '',
        razaoSocial: prestador.razaoSocial ?? '',
        cep: prestador.cep ?? '',
        logradouro: prestador.logradouro ?? '',
        numero: prestador.numero ?? '',
        complemento: prestador.complemento ?? '',
        bairro: prestador.bairro ?? '',
        cidade: prestador.cidade ?? '',
        uf: prestador.uf ?? '',
        bancoNome: prestador.bancoNome ?? '',
        bancoAgencia: prestador.bancoAgencia ?? '',
        bancoConta: prestador.bancoConta ?? '',
        bancoTipoConta: prestador.bancoTipoConta ?? '',
        pixChave: prestador.pixChave ?? '',
        areaAtuacao: prestador.areaAtuacao ?? '',
        dataInicioContrato: prestador.dataInicioContrato?.slice(0, 10) ?? '',
        status: prestador.status,
        observacoes: prestador.observacoes ?? '',
        portalUserId: prestador.portalUserId ?? '',
      })
    }
    setShowFormModal(true)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (formData.id) {
        await pjAgradaService.update(formData.id, formData)
      } else {
        await pjAgradaService.create(formData)
      }
      setShowFormModal(false)
      await load()
      toast.success(`Funcionário PJ ${formData.id ? 'atualizado' : 'cadastrado'} com sucesso`)
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao salvar funcionário PJ'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este funcionário PJ?')) return
    try {
      await pjAgradaService.delete(id)
      if (selected?.id === id) setSelected(null)
      await load()
      toast.success('Funcionário PJ excluído')
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao excluir funcionário PJ'))
    }
  }

  return {
    prestadores: filtered,
    stats,
    loading,
    search,
    setSearch,
    filter,
    setFilter,
    showFormModal,
    setShowFormModal,
    formData,
    setFormData,
    saving,
    selected,
    setSelected,
    reload: load,
    openForm,
    handleSave,
    handleDelete,
  }
}
